import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ThemeToggle from "@/components/ThemeToggle";
import { ChevronDown, Hotel, Sun, Footprints, Menu, X, PawPrint } from "lucide-react";
import { useLeadCapture } from "@/context/LeadCaptureContext";

const FEATURE_LINKS = [
  {
    icon: Hotel,
    title: "Kennel & Hotellhantering",
    desc: "Rumstilldelning, kost & medicin.",
    to: "/hundpensionat-system",
    testid: "feature-dropdown-kennel",
  },
  {
    icon: Sun,
    title: "Hunddagis",
    desc: "Närvaro, fakturering, ägaruppdateringar.",
    to: "/hunddagis-app",
    testid: "feature-dropdown-daycare",
  },
  {
    icon: Footprints,
    title: "Hundpromenader",
    desc: "GPS-spår, schema & hundvakter.",
    to: "/hundrastning-program",
    testid: "feature-dropdown-walking",
  },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { openLeadCapture } = useLeadCapture();
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? "backdrop-blur-xl bg-white/85 dark:bg-slate-950/85 border-b border-slate-200/60 dark:border-slate-800/60"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="flex h-20 items-center justify-between">
          <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2 group">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-900 dark:bg-emerald-500 text-white transition-transform group-hover:rotate-6">
              <PawPrint className="h-5 w-5" />
            </div>
            <span className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
              Hundify
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="nav-features-trigger"
                className="inline-flex items-center gap-1 rounded-lg px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors outline-none focus:ring-2 focus:ring-emerald-500/30"
              >
                Funktioner <ChevronDown className="h-4 w-4 opacity-70" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="w-[360px] p-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl"
              >
                {FEATURE_LINKS.map(({ icon: Icon, title, desc, to, testid }) => (
                  <DropdownMenuItem
                    key={title}
                    data-testid={testid}
                    asChild
                    className="rounded-xl p-3 cursor-pointer focus:bg-emerald-50 dark:focus:bg-emerald-500/10"
                  >
                    <Link to={to} className="flex items-start gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 shrink-0">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900 dark:text-white text-sm">{title}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{desc}</div>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link
              to="/pricing"
              data-testid="nav-pricing"
              className="rounded-lg px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors"
            >
              Priser
            </Link>
            <a
              href={isHome ? "#testimonials" : "/#testimonials"}
              data-testid="nav-customers"
              className="rounded-lg px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors"
            >
              Kunder
            </a>
          </nav>

          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Button
              data-testid="nav-boka-demo"
              onClick={() => openLeadCapture("Boka en Demo")}
              className="hidden sm:inline-flex bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl px-5 h-10 font-semibold shadow-[0_4px_14px_0_rgba(16,185,129,0.39)] hover:shadow-[0_6px_22px_rgba(16,185,129,0.45)] hover:-translate-y-0.5 transition-all"
            >
              Boka en Demo
            </Button>
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 dark:border-slate-700"
              onClick={() => setMobileOpen((o) => !o)}
              aria-label="Meny"
            >
              {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div data-testid="mobile-menu" className="lg:hidden pb-6 pt-2 space-y-2">
            {FEATURE_LINKS.map((f) => (
              <Link key={f.to} to={f.to} className="block rounded-xl px-4 py-3 text-sm font-medium bg-slate-50 dark:bg-slate-900">{f.title}</Link>
            ))}
            <Link to="/pricing" className="block rounded-xl px-4 py-3 text-sm font-medium bg-slate-50 dark:bg-slate-900">Priser</Link>
            <Button
              data-testid="mobile-boka-demo"
              onClick={() => openLeadCapture("Mobil meny")}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl h-12 font-semibold"
            >
              Boka en Demo
            </Button>
          </div>
        )}
      </div>
    </header>
  );
}
